import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [adminInfo, setAdminInfo] = useState(null);
  const [analytics, setAnalytics] = useState({ 
    total_s3_posters: 0,
    total_original_images: 0,
    total_cropped_images: 0
  });
  const [filters, setFilters] = useState({
    stage: 'all',
    search: '',
    startDate: '',
    endDate: '',
    page: 1,
    limit: 50
  });
  
  // Real-time polling state
  const [isRealTimeEnabled, setIsRealTimeEnabled] = useState(true);
  const [pollingInterval, setPollingInterval] = useState(30); // seconds
  const [lastUpdated, setLastUpdated] = useState(null);
  const [pollingTimer, setPollingTimer] = useState(null);
  
  const navigate = useNavigate();

  useEffect(() => {
    checkAuthStatus();
  }, []);

  useEffect(() => {
    fetchLeads();
  }, [filters]);

  // Real-time polling effect
  useEffect(() => {
    if (!isRealTimeEnabled) {
      if (pollingTimer) {
        clearInterval(pollingTimer);
        setPollingTimer(null);
      }
      return;
    }

    // Start polling
    const timer = setInterval(() => {
      fetchLeads(true); // Silent refresh (no loading state)
    }, pollingInterval * 1000);

    setPollingTimer(timer);

    // Cleanup on unmount or when dependencies change
    return () => {
      if (timer) {
        clearInterval(timer);
      }
    };
  }, [isRealTimeEnabled, pollingInterval]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingTimer) {
        clearInterval(pollingTimer);
      }
    };
  }, []);

  const checkAuthStatus = async () => {
    try {
      const response = await fetch('/admin/auth-check.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ action: 'status' }),
        credentials: 'include'
      });
      const data = await response.json();
      
      if (!data.isAuthenticated) {
        navigate('/admin/login');
      } else {
        setAdminInfo(data.admin);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      navigate('/admin/login');
    }
  };

  const fetchLeads = async (silentRefresh = false) => {
    if (!silentRefresh) {
      setLoading(true);
    }
    setError('');
    
    try {
      const queryParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) queryParams.append(key, value);
      });

      const response = await fetch(`/admin/leads.php?${queryParams}`, {
        credentials: 'include'
      });
      const data = await response.json();
        if (data.success) {
        setLeads(data.data);
        if (data.analytics) {
          setAnalytics(data.analytics);
        }
        setLastUpdated(new Date());
      } else {
        setError(data.message || 'Failed to fetch leads');
      }
    } catch (error) {
      setError('Network error occurred');
      console.error('Fetch leads error:', error);
    } finally {
      if (!silentRefresh) {
        setLoading(false);
      }
    }
  };

  const handleLogout = async () => {
    try {
      await fetch('/admin/logout.php', { 
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ action: 'logout' }),
        credentials: 'include'
      });
      localStorage.removeItem('adminAuthenticated');
      localStorage.removeItem('adminUser');
      navigate('/admin/login');
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/admin/login');
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value,
      page: 1 // Reset to first page when filters change
    }));
  };
  const handleExport = async (exportType = 'filtered') => {
    try {
      const queryParams = new URLSearchParams();
      
      // Always add export type
      queryParams.append('exportType', exportType);
      
      // Only add filters if it's not an "All Time" export
      if (exportType !== 'all_time') {
        Object.entries(filters).forEach(([key, value]) => {
          if (value && key !== 'page' && key !== 'limit') {
            queryParams.append(key, value);
          }
        });
      }

      const response = await fetch(`/admin/export.php?${queryParams}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const filename = exportType === 'all_time' 
          ? `ltf-leads-all-time-${new Date().toISOString().split('T')[0]}.csv`
          : `ltf-leads-${new Date().toISOString().split('T')[0]}.csv`;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else {
        setError('Export failed');
      }
    } catch (error) {
      setError('Export failed');
      console.error('Export error:', error);
    }
  };

  const handleManualRefresh = () => {
    fetchLeads();
  };

  const toggleRealTime = () => {
    setIsRealTimeEnabled(!isRealTimeEnabled);
  };

  const handlePollingIntervalChange = (e) => {
    const newInterval = parseInt(e.target.value);
    setPollingInterval(newInterval);
  };

  const formatLastUpdated = () => {
    if (!lastUpdated) return 'Never';
    const now = new Date();
    const diffMs = now - lastUpdated;
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    
    if (diffSecs < 60) {
      return `${diffSecs} seconds ago`;
    } else if (diffMins < 60) {
      return `${diffMins} minutes ago`;
    } else {
      return lastUpdated.toLocaleTimeString();
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const getStageColor = (stage) => {
    const colors = {
      'phone': '#ff9800',
      'otp': '#2196f3',
      'details': '#9c27b0',
      'upload': '#f44336',
      'poster': '#4caf50'
    };
    return colors[stage] || '#757575';
  };

  const getStageIcon = (stage) => {
    const icons = {
      'phone': '📱',
      'otp': '🔐',
      'details': '📝',
      'upload': '📸',
      'poster': '🎨'
    };
    return icons[stage] || '❓';
  };

  return (
    <div className="admin-dashboard">
      <header className="admin-header">
        <div className="header-left">
          <h1>L&T Finance Admin Panel</h1>
          {adminInfo && (
            <span className="admin-info">Welcome, {adminInfo.username}</span>
          )}
        </div>
        <button onClick={handleLogout} className="logout-btn">
          Logout
        </button>
      </header>      <div className="admin-content">
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {/* Analytics Section */}
        <div className="analytics-section">
          <div className="analytics-card">
            <h3>📊 S3 Posters Generated</h3>
            <div className="analytics-value">{analytics.total_s3_posters}</div>
            <p>Total posters successfully uploaded to S3</p>
          </div>
          <div className="analytics-card">
            <h3>📸 Original Images</h3>
            <div className="analytics-value">{analytics.total_original_images}</div>
            <p>User uploads, camera photos & selfies</p>
          </div>
          <div className="analytics-card">
            <h3>✂️ Cropped Previews</h3>
            <div className="analytics-value">{analytics.total_cropped_images}</div>
            <p>Images processed for poster generation</p>
          </div>
        </div>

        <div className="leads-section">
          <div className="leads-header">
            <div className="leads-header-left">
              <h2>Leads Management</h2>
              <div className="real-time-controls">
                <div className="real-time-status">
                  <span className={`status-indicator ${isRealTimeEnabled ? 'active' : 'inactive'}`}></span>
                  <span>Last updated: {formatLastUpdated()}</span>
                </div>
                <button 
                  onClick={handleManualRefresh} 
                  className="refresh-btn"
                  disabled={loading}
                >
                  🔄 Refresh
                </button>
              </div>
            </div>
            <div className="leads-header-right">
              <div className="real-time-toggle">
                <label>
                  <input
                    type="checkbox"
                    checked={isRealTimeEnabled}
                    onChange={toggleRealTime}
                  />
                  Real-time updates
                </label>
                {isRealTimeEnabled && (
                  <select
                    value={pollingInterval}
                    onChange={handlePollingIntervalChange}
                    className="polling-interval"
                  >
                    <option value={10}>Every 10s</option>
                    <option value={30}>Every 30s</option>
                    <option value={60}>Every 1m</option>
                    <option value={300}>Every 5m</option>
                  </select>
                )}
              </div>
            </div>
          </div>

          <div className="filters-section">
            <div className="filters-row">
              <select
                name="stage"
                value={filters.stage}
                onChange={handleFilterChange}
                className="filter-select"
              >
                <option value="all">All Stages</option>
                <option value="phone">Phone Entry</option>
                <option value="otp">OTP Verification</option>
                <option value="details">Details Form</option>
                <option value="upload">Photo Upload</option>
                <option value="poster">Poster Generated</option>
              </select>

              <input
                type="text"
                name="search"
                placeholder="Search by phone, name, or company..."
                value={filters.search}
                onChange={handleFilterChange}
                className="filter-input"
              />

              <input
                type="date"
                name="startDate"
                value={filters.startDate}
                onChange={handleFilterChange}
                className="filter-input"
              />

              <input
                type="date"
                name="endDate"
                value={filters.endDate}
                onChange={handleFilterChange}
                className="filter-input"
              />              <button onClick={() => handleExport('filtered')} className="export-btn">
                📊 Export CSV (Filtered)
              </button>
              
              <button onClick={() => handleExport('all_time')} className="export-btn export-all-time">
                📊 Export All Time
              </button>
            </div>
          </div>

          {loading ? (
            <div className="loading">Loading leads...</div>
          ) : (
            <div className="leads-table-container">              <table className="leads-table">
                <thead>
                  <tr>
                    <th>Session Start</th>
                    <th>Phone Number</th>
                    <th>OTP Verified</th>
                    <th>OTP Verification Time</th>
                    <th>Username</th>
                    <th>Company Name</th>
                    <th>Industry</th>
                    <th>Keywords</th>
                    <th>Tagline</th>
                    <th>Image Upload Type</th>
                    <th>Original Image</th>
                    <th>Cropped Preview</th>
                    <th>Poster Generated</th>
                    <th>Poster S3 URL</th>
                    <th>Poster Downloaded</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.length > 0 ? (
                    leads.map((lead) => (
                      <tr key={lead.id}>
                        <td>{formatDate(lead.session_start)}</td>
                        <td>{lead.phone_number || 'N/A'}</td>
                        <td>{lead.otp_verified ? '✅' : '❌'}</td>
                        <td>{formatDate(lead.otp_verification_time)}</td>
                        <td>{lead.user_name || 'N/A'}</td>
                        <td>{lead.company_name || 'N/A'}</td>
                        <td>{lead.industry || 'N/A'}</td>
                        <td>
                          <div className="keywords-cell">
                            {lead.keywords && lead.keywords.length > 0 
                              ? lead.keywords.join(', ') 
                              : 'N/A'
                            }
                          </div>
                        </td>
                        <td>
                          <div className="tagline-cell" title={lead.tagline || 'N/A'}>
                            {lead.tagline ? 
                              (lead.tagline.length > 50 ? 
                                lead.tagline.substring(0, 50) + '...' : 
                                lead.tagline
                              ) : 'N/A'
                            }
                          </div>
                        </td>
                        <td>
                          <span className={`upload-type-badge ${lead.image_upload_type || 'unknown'}`}>
                            {lead.image_upload_type ? 
                              (lead.image_upload_type === 'upload' ? '📁 File' : 
                               lead.image_upload_type === 'camera' ? '📷 Camera' : 
                               lead.image_upload_type === 'selfie' ? '🤳 Selfie' : 
                               '❓ Unknown') : 'N/A'}
                          </span>
                        </td>
                        <td>
                          {lead.original_image_s3_url ? (
                            <a href={lead.original_image_s3_url} target="_blank" rel="noopener noreferrer" className="url-link">
                              View Original
                            </a>
                          ) : 'N/A'}
                        </td>
                        <td>
                          {lead.cropped_image_s3_url ? (
                            <a href={lead.cropped_image_s3_url} target="_blank" rel="noopener noreferrer" className="url-link">
                              View Cropped
                            </a>
                          ) : 'N/A'}
                        </td>
                        <td>{lead.poster_generated ? '✅' : '❌'}</td>
                        <td>
                          {lead.poster_s3_url ? (
                            <a href={lead.poster_s3_url} target="_blank" rel="noopener noreferrer" className="url-link">
                              View Poster
                            </a>
                          ) : 'N/A'}
                        </td>
                        <td>{lead.poster_downloaded ? '✅' : '❌'}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="15" className="no-data">No leads found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
